import { AnimatePresence, motion } from 'framer-motion';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box, Chip, IconButton, Modal, ModalDialog, Stack, Typography, useTheme,
} from '@mui/joy';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

import { MOTION, ellipsis, clampLines, thinScrollbar, avatarGradient, initialsOf } from '../MessagesChat/chatTheme';
import { useVisualViewport } from '../../hooks/useVisualViewport';

/**
 * Список всех закреплённых сообщений чата.
 *
 * Нужен, когда закреплённых много: листать их по одному через полоску
 * становится дольше, чем открыть список и выбрать глазами. Нажатие на
 * строку закрывает окно и переводит к сообщению в ленте — именно этого
 * ждёшь от такого списка.
 *
 * Открепить можно прямо отсюда: чаще всего сюда и заходят, чтобы
 * разобрать накопившееся.
 */
export default function ModalPinnedList({
  open, onClose, pinned = [], onJump, onUnpin,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Высота видимой области, а не экрана — на случай открытой клавиатуры.
  const { height: vvHeight } = useVisualViewport();
  const mobileHeight = vvHeight ? `${vvHeight}px` : '100dvh';

  return (
    <AnimatePresence>
      {open && (
        <Modal
          open={open}
          onClose={onClose}
          sx={{
            zIndex: 10210,
            // Центрируем ЗДЕСЬ, на самой модалке. Собственное центрирование
            // Joy у ModalDialog — это absolute + translate(-50%,-50%), и
            // framer затирает translate своим transform из animate. Ровно
            // поэтому окно и прилипало к левому верхнему углу.
            //
            // В ModalChatInfo эту роль играет ModalOverflow — он флекс, — а
            // здесь его нет намеренно: обёртка-со-скроллом вокруг
            // полноэкранного окна на телефоне уже приводила к тому, что
            // прокручивается всё окно целиком.
            ...(isMobile ? null : {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }),
          }}
          slotProps={{
            backdrop: {
              component: motion.div,
              initial: { opacity: 0, backdropFilter: 'blur(0px)' },
              animate: { opacity: 1, backdropFilter: 'blur(8px)' },
              exit: { opacity: 0, backdropFilter: 'blur(0px)' },
              transition: { duration: 0.2, ease: 'easeInOut' },
            },
          }}
        >
          <ModalDialog
            layout={isMobile ? 'fullscreen' : 'center'}
            size="md"
            component={motion.div}
            initial={{ opacity: 0, y: 18, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            sx={{
              width: isMobile ? '100%' : 460,
              maxWidth: '100%',
              maxHeight: isMobile ? mobileHeight : '78vh',
              p: 0,
              borderRadius: { xs: 0, sm: '22px' },
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              // Своё позиционирование Joy снимаем — за центр теперь отвечает
              // флекс на самой Modal (см. комментарий выше). На мобиле
              // layout="fullscreen" позиционируется через inset, туда не лезем.
              ...(isMobile ? null : {
                position: 'relative',
                top: 'auto', left: 'auto',
                transform: 'none',
                m: 0,
              }),
            }}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: 'center', justifyContent: 'space-between',
                px: 2, py: 1.75, flexShrink: 0,
                borderBottom: '1px solid', borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                <PushPinRoundedIcon sx={{ fontSize: 18, color: 'primary.500', transform: 'rotate(35deg)' }} />
                <Typography level="title-sm" sx={{ fontWeight: 750 }}>
                  Закреплённые
                </Typography>
                <Chip size="sm" variant="soft" color="primary">{pinned.length}</Chip>
              </Stack>
              <IconButton size="sm" variant="plain" onClick={onClose} sx={{ borderRadius: '50%' }}>
                <CloseRoundedIcon />
              </IconButton>
            </Stack>

            <Box
              sx={(t) => ({
                flex: 1, minHeight: 0, minWidth: 0,
                // Только вертикаль: длинная реплика не должна давать
                // возможность таскать список вбок.
                overflowY: 'auto', overflowX: 'hidden',
                overscrollBehavior: 'contain',
                WebkitOverflowScrolling: 'touch',
                p: 1,
                ...thinScrollbar(t),
              })}
            >
              {pinned.length === 0 ? (
                <Stack alignItems="center" spacing={1} sx={{ py: 6, px: 3 }}>
                  <PushPinOutlinedIcon sx={{ fontSize: 34, color: 'text.tertiary', opacity: 0.5 }} />
                  <Typography level="body-sm" sx={{ textAlign: 'center', color: 'text.tertiary' }}>
                    Закреплённых сообщений нет
                  </Typography>
                </Stack>
              ) : pinned.map((p, i) => (
                <Stack
                  key={p.id}
                  component={motion.div}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: MOTION.fast, delay: Math.min(i, 10) * 0.02 }}
                  direction="row"
                  spacing={1.25}
                  onClick={() => { onJump?.(p.id); onClose?.(); }}
                  sx={{
                    alignItems: 'flex-start',
                    minWidth: 0,
                    px: 1.25, py: 1,
                    borderRadius: '14px',
                    cursor: 'pointer',
                    transition: 'background-color .16s',
                    '&:hover': { backgroundColor: 'background.level1' },
                  }}
                >
                  <Box
                    sx={{
                      width: 30, height: 30, flexShrink: 0, mt: 0.25,
                      borderRadius: '50%',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 800, color: '#fff',
                      background: avatarGradient(p.author || 'Чат'),
                    }}
                  >
                    {initialsOf(p.author || '')}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      level="body-xs"
                      sx={{ ...ellipsis, fontWeight: 700, fontSize: 12, color: 'text.secondary' }}
                    >
                      {p.author || 'Системное сообщение'}
                    </Typography>
                    <Typography
                      level="body-sm"
                      sx={{ ...clampLines(2), fontSize: 13, color: 'text.primary', mt: 0.125 }}
                    >
                      {p.content || 'Сообщение'}
                    </Typography>
                  </Box>

                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    title="Открепить"
                    onClick={(e) => { e.stopPropagation(); onUnpin?.(p.id); }}
                    sx={{ flexShrink: 0, borderRadius: '50%' }}
                  >
                    <PushPinRoundedIcon
                      sx={{ fontSize: 16, color: 'primary.500', transform: 'rotate(35deg)' }}
                    />
                  </IconButton>
                </Stack>
              ))}
            </Box>
          </ModalDialog>
        </Modal>
      )}
    </AnimatePresence>
  );
}
